/**
 * SettingsDashboard — the launcher shown at /app/settings.
 *
 * Redesigned Feb 2026 for 100% browser zoom on 1366×768 → 1920×1080.
 * Cards are spacious, titles are large, descriptions read at their full length
 * without truncation.  Grid: 1 col on mobile, 2 on medium, 3 on wide — never
 * more (a 4-col grid packed too tight at default zoom).
 */
import { Link } from "react-router-dom";
import { CATEGORIES } from "./registry";
import { ChevronRight, Sparkles } from "lucide-react";

// Highlighted tiles for a curated "Popular" strip at the top.
const POPULAR = [
  ["general", "clinic-profile"],
  ["general", "branding"],
  ["appointments", "online-booking"],
  ["billing", "invoice-settings"],
  ["clinical", "procedures"],
  ["integrations", "google-calendar"],
  ["communication", "whatsapp"],
  ["appearance", "theme"],
];

export default function SettingsDashboard() {
  // Hidden sections are ones we've built the placeholder for but haven't
  // released yet. They MUST NOT render as tiles or in category counts —
  // otherwise the UI advertises features that don't exist.
  const isVisible = (s) => s.status !== "hidden";
  const popularTiles = POPULAR.map(([c, s]) => {
    const cat = CATEGORIES.find((x) => x.slug === c);
    const sec = cat?.sections.find((x) => x.slug === s);
    if (!cat || !sec || !isVisible(sec)) return null;
    return { ...sec, cat: cat.label, categorySlug: cat.slug };
  }).filter(Boolean);

  return (
    <div className="space-y-14" data-testid="settings-dashboard">
      <header>
        <h1 className="font-display text-4xl md:text-5xl text-ink tracking-tight">Settings</h1>
        <p className="mt-3 text-base text-ink-mid max-w-2xl leading-relaxed">
          Configure your clinic. Everything lives inside this workspace — pick a category on
          the left, or start with a popular section below.
        </p>
      </header>

      {/* Popular */}
      <section>
        <div className="flex items-center gap-2 mb-5">
          <Sparkles className="w-4 h-4 text-pine" />
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-ink-soft">Popular</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5">
          {popularTiles.map((t) => (
            <Tile key={`${t.categorySlug}:${t.slug}`} tile={t} />
          ))}
        </div>
      </section>

      {/* Full launcher */}
      {CATEGORIES.map((cat) => {
        const visibleSections = cat.sections.filter(isVisible);
        if (visibleSections.length === 0) return null;
        return (
          <section key={cat.slug} id={`cat-${cat.slug}`}>
            <div className="flex items-baseline justify-between mb-5 pb-3 border-b border-[#EFEADC]">
              <h2 className="font-display text-2xl text-ink tracking-tight">{cat.label}</h2>
              <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-soft">
                {visibleSections.length} settings
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5">
              {visibleSections.map((s) => (
                <Tile
                  key={s.slug}
                  tile={{ ...s, cat: cat.label, categorySlug: cat.slug }}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}


function Tile({ tile }) {
  const Icon = tile.icon;
  const to = `/app/settings/${tile.categorySlug}/${tile.slug}`;
  const cls =
    "group flex items-start gap-4 p-5 lg:p-6 rounded-2xl border transition-all duration-200 min-h-[112px] " +
    "border-[#EFEADC] bg-white hover:border-pine/40 " +
    "hover:shadow-[0_10px_30px_-16px_rgba(26,54,43,0.22)] hover:-translate-y-0.5 cursor-pointer";
  return (
    <Link data-testid={`tile-${tile.slug}`} to={to} className={cls}>
      <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0 transition-colors bg-linen text-ink group-hover:bg-pine group-hover:text-white">
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-ink text-[15px] leading-snug">{tile.title}</div>
        <div className="text-[13px] text-ink-mid mt-1.5 leading-[1.55]">{tile.desc}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-ink-disabled group-hover:text-pine group-hover:translate-x-1 transition-all shrink-0 mt-1" />
    </Link>
  );
}
